import React, { useEffect, useState, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AdminDataService from "../services/admin.service";
import RechercheDataService from "../services/recherche.service";
import ManagerDataService from "../services/manager.service";
import { setAuthToken } from "../http-common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useLocation, useParams } from "react-router-dom";
import Select from 'react-select';

const UserProfil = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();
  const { email: emailParam } = useParams();
  const urlEmail = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search);
      const q = (qs.get('email') || '').trim();
      return (emailParam || q || '').trim();
    } catch {
      return (emailParam || '').trim();
    }
  }, [emailParam, location.search]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profil, setProfil] = useState({
    nom: "",
    prenom: "",
    sexe: "",
    date_naiss: "",
    nationalite: "",
    residence: "",
    tel: ""
  });
  const [subTerrs, setSubTerrs] = useState([]);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [habilitations, setHabilitations] = useState([]);
  const [roles, setRoles] = useState(null);
  const [rightsLoading, setRightsLoading] = useState(false);
  const [habilLight, setHabilLight] = useState({ admin: null, recette: null });
  const [showHabiliter, setShowHabiliter] = useState(false);
  const [habBusy, setHabBusy] = useState(false);
  const [habTitre, setHabTitre] = useState("");
  const [habMatricule, setHabMatricule] = useState("");
  const [habSpecs, setHabSpecs] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [showConfirmRevoke, setShowConfirmRevoke] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [habAdminBusy, setHabAdminBusy] = useState(false);
  const [habRecetteBusy, setHabRecetteBusy] = useState(false);
  const [showRecetteRequiresAdmin, setShowRecetteRequiresAdmin] = useState(false);
  const isAdmin = useMemo(() => Number(roles?.admin) === 1, [roles]);
  const isSupAdmin = useMemo(() => Number(roles?.supadmin) === 1, [roles]);
  const groupedHabilitations = useMemo(() => {
    const groups = habilitations.reduce((acc, h) => {
      const k = h.libelle_structure || "—";
      (acc[k] = acc[k] || []).push(h);
      return acc;
    }, {});
    const entries = Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    entries.forEach(([_, arr]) => arr.sort((a, b) => String(a.libelle_service || "").localeCompare(String(b.libelle_service || ""))));
    return entries;
  }, [habilitations]);

  const loadProfil = async () => {
    setError("");
    if (!urlEmail) {
      // Toujours charger les droits pour savoir si l'utilisateur est admin
      setRightsLoading(true);
      try {
        const r = await ManagerDataService.habilitation(user.email);
        const arr = Array.isArray(r?.data) ? r.data : [];
        setRoles(arr.length ? arr[0] : {});
      } catch (e) {
        console.error("manager.habilitation failed", e?.response?.data || e?.message);
        setRoles({});
      } finally {
        setRightsLoading(false);
      }
      // Charger habilitations admin/recette pour l'utilisateur cible s'il y a un email
      if (urlEmail) {
        try {
          const hl = await AdminDataService.getHabilAdminRecetteByEmail(urlEmail);
          setHabilLight(hl?.data || { admin: null, recette: null });
        } catch {}
      }
      setLoading(false);
      return;
    }
    try {
      const res = await AdminDataService.getUserNameByEmail(urlEmail);
      const p = res?.data || {};
      setProfil({
        nom: p.nom || "",
        prenom: p.prenom || "",
        sexe: (p.sexe || "").toUpperCase(),
        date_naiss: p.date_naiss ? String(p.date_naiss).slice(0, 10) : "",
        nationalite: p.nationalite || "",
        residence: p.residence || "",
        tel: p.tel || ""
      });
      try {
        const subRes = await AdminDataService.list('sub-terr');
        setSubTerrs(subRes?.data || []);
      } catch {}
      // Charger informations médecin (titre, matricule, spécialités) si existantes
      try {
        const resDoc = await RechercheDataService.showDoctor2(urlEmail);
        const arr = resDoc?.data || [];
        setDoctorInfo(arr && arr.length ? arr[0] : null);
      } catch {}
      // Charger mes habilitations (liens Utilisateur/Service actifs)
      try {
        const us = await AdminDataService.getUserServiceByEmail(urlEmail);
        setHabilitations(Array.isArray(us?.data) ? us.data : []);
      } catch (e) {
        console.error("getUserServiceByEmail failed", e?.response?.data || e?.message);
      }
      setRightsLoading(true);
      try {
        const r = await ManagerDataService.habilitation(user.email);
        const arr = Array.isArray(r?.data) ? r.data : [];
        setRoles(arr.length ? arr[0] : {});
      } catch (e) {
        console.error("manager.habilitation failed", e?.response?.data || e?.message);
        setRoles({});
      } finally {
        setRightsLoading(false);
      }
      // Charger habilitations admin/recette pour l'utilisateur cible
      try {
        const hl = await AdminDataService.getHabilAdminRecetteByEmail(urlEmail);
        setHabilLight(hl?.data || { admin: null, recette: null });
      } catch {}
    } catch {
      setError("Impossible de charger le profil");
      setRoles({});
      setRightsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        });
        if (token && token.split(".").length === 3) setAuthToken(token);
      } catch {}
      await loadProfil();
    };
    if (user?.email) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, urlEmail]);

  return (
    <div className="container-fluid">
      {/* Bannière (même style que ConfirmRdv) */}
      <div className="row" style={{ backgroundColor: "#6f42c1", color: "white", padding: "20px" }}>
        <span style={{ fontSize: "1.5rem" }} className="lead">
          <FontAwesomeIcon icon={solid('user')} className="me-2" />
          <b>Profil d'un utilisateur</b>
        </span>
      </div>

      {/* Contenu (même organisation de blocs que ConfirmRdv) */}
      {(() => {
        const ready = !rightsLoading && roles != null;
        const adminOk = ready && Number(roles?.admin) === 1;
        if (!ready) {
          return <div className="p-4"><div className="text-muted">Chargement des droits...</div></div>;
        }
        if (!adminOk) {
          return (
            <div className="p-4">
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <span className="me-2"><FontAwesomeIcon icon={solid('triangle-exclamation')} /></span>
                <div><strong>Accès interdit.</strong> Vous n'avez pas les droits nécessaires pour accéder à cette section.</div>
              </div>
            </div>
          );
        }
        if (!urlEmail) {
          return (
            <div className="p-4">
              <div className="alert alert-warning d-flex align-items-center" role="alert">
                <span className="me-2"><FontAwesomeIcon icon={solid('triangle-exclamation')} /></span>
                <div>Aucun email n'a été fourni dans l'URL. Veuillez ouvrir ce profil depuis la liste des utilisateurs.</div>
              </div>
            </div>
          );
        }
        return (
          <div className="row" style={{ backgroundColor: "#E6EAF7", padding: "40px" }}>
            <div className="col-6" style={{ backgroundColor: "white", padding: "40px" }}>
              {error && <div className="text-danger mb-2">{error}</div>}
              <span style={{ fontSize: "1.2rem" }} className="lead"><b>Informations</b></span>
              <hr />
              <ul className="list-unstyled">
                <li className="lead"><b>Nom:</b> {profil.nom || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Prénom:</b> {profil.prenom || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Sexe:</b> {profil.sexe || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Date de naissance:</b> {profil.date_naiss || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Nationalité:</b> {profil.nationalite || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Résidence:</b> {(subTerrs.find(st => String(st.id) === String(profil.residence))?.libelle_sub_terr) || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Téléphone:</b> {profil.tel || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Email:</b> {urlEmail || <span className="text-muted">—</span>}</li>
              </ul>
            </div>
            <div className="col-6" style={{ backgroundColor: "white", padding: "40px" }}>
              {error && <div className="text-danger mb-2">{error}</div>}
              <span style={{ fontSize: "1.2rem" }} className="lead"><b>Habilitations</b></span>
              <hr />
              {!isSupAdmin && !!habilLight?.admin && (
                <span className="ms-2 badge bg-warning text-dark">
                  <FontAwesomeIcon icon={solid('user-shield')} className="me-1" />
                  Admin
                </span>
              )}
              {isSupAdmin && (
                <>
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swAdmin"
                      checked={!!habilLight?.admin}
                      disabled={habAdminBusy}
                      onChange={async (e) => {
                        const turnOn = e.target.checked;
                        setHabAdminBusy(true);
                        try {
                          if (turnOn) {
                            await AdminDataService.enableHabilAdmin({ targetEmail: urlEmail, email: user.email });
                          } else {
                            if (habilLight?.admin) {
                              await AdminDataService.disableHabilAdmin(habilLight.admin, { email: user.email, recetteId: habilLight?.recette || null });
                            }
                          }
                          await loadProfil();
                        } catch (err) {
                        } finally {
                          setHabAdminBusy(false);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="swAdmin">Administrateur</label>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="swRecette"
                      checked={!!habilLight?.recette}
                      disabled={habRecetteBusy}
                      onChange={async (e) => {
                        const turnOn = e.target.checked;
                        if (!habilLight?.admin) {
                          e.preventDefault();
                          setShowRecetteRequiresAdmin(true);
                          return;
                        }
                        setHabRecetteBusy(true);
                        try {
                          if (turnOn) {
                            await AdminDataService.enableHabilRecette({ targetEmail: urlEmail, email: user.email });
                          } else {
                            if (habilLight?.recette) {
                              await AdminDataService.disableHabilRecette(habilLight.recette, { email: user.email });
                            }
                          }
                          await loadProfil();
                        } catch (err) {
                        } finally {
                          setHabRecetteBusy(false);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="swRecette">Visibilité sur les recettes</label>
                  </div>
                </>
              )}
              {doctorInfo ? (
                <div className="p-3 mt-2" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-success">
                      <FontAwesomeIcon icon={solid('user-doctor')} className="me-1" />
                      {doctorInfo.titre || '—'}
                    </span>
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-light border p-0"
                        style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
                        title="Modifier l'habilitation"
                        aria-label="Modifier"
                        onClick={async () => {
                          // Ensure specialites options are loaded
                          try {
                            if (!specialites || !specialites.length) {
                              const res = await AdminDataService.list('specialites');
                              setSpecialites(Array.isArray(res?.data) ? res.data : []);
                            }
                          } catch {}
                          // Prefill titre/matricule
                          setHabTitre(doctorInfo?.titre || '');
                          setHabMatricule(doctorInfo?.matricule || '');
                          // Prefill specs by matching labels from doctorInfo.spec (comma-separated names)
                          const specNames = String(doctorInfo?.spec || '')
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean);
                          const opts = (specialites || []).map(s => ({ value: s.id, label: s.libelle_specialite }));
                          const preselected = opts.filter(o => specNames.some(n => n.toLowerCase() === String(o.label || '').toLowerCase()));
                          setHabSpecs(preselected);
                          setShowHabiliter(true);
                        }}
                      >
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-light border p-0"
                        style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
                        title="Supprimer l'habilitation"
                        aria-label="Supprimer"
                        onClick={() => setShowConfirmRevoke(true)}
                      >
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </div>
                  </div>
                  <ul className="list-unstyled mb-0 mt-2">
                    <li className="lead"><b>Matricule:</b> {doctorInfo.matricule || <span className="text-muted">—</span>}</li>
                    <li className="lead"><b>Spécialité(s):</b> {doctorInfo.spec || <span className="text-muted">—</span>}</li>
                  </ul>
                </div>
              ) : (
                <a
                  href="#"
                  className="ms-2 link-primary"
                  title="Habiliter cet utilisateur comme médecin"
                  aria-label="Habiliter"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await AdminDataService.list('specialites');
                      setSpecialites(Array.isArray(res?.data) ? res.data : []);
                    } catch {}
                    setHabTitre('');
                    setHabMatricule('');
                    setHabSpecs([]);
                    setShowHabiliter(true);
                  }}
                >
                  <FontAwesomeIcon icon={solid('user-doctor')} className="me-2" />
                  Habiliter comme médecin
                </a>
              )}
              <div className="p-3 mt-2" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                {(!habilitations || !habilitations.length) && (
                  <div className="text-muted">Aucun lien utilisateur/service actif</div>
                )}
                {groupedHabilitations && groupedHabilitations.length > 0 && (
                  <div>
                    {groupedHabilitations.map(([structure, items]) => (
                      <div key={structure} className="mb-3">
                        <div className="text-uppercase fw-bold text-secondary small">{structure}</div>
                        <ul className="list-unstyled mb-0">
                          {items.map((h, idx) => (
                            <li key={h.id} className={idx === items.length - 1 ? "py-1" : "py-1 border-bottom"}>
                              <span className="lead"><b>{h.libelle_service || <span className="text-muted">—</span>}</b></span>
                              <span className="ms-2 badge bg-primary">
                                {Number(h.recette) === 1 && (
                                  <FontAwesomeIcon icon={solid('dollar-sign')} className="me-1" />
                                )}
                                {h.role || '—'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {showHabiliter && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!habTitre || !habMatricule.trim() || !(habSpecs && habSpecs.length)) return;
                  setHabBusy(true);
                  try {
                    await AdminDataService.habiliterMedecin({
                      targetEmail: urlEmail,
                      titre: habTitre,
                      matricule: habMatricule.trim(),
                      specialiteIds: habSpecs.map(o => Number(o.value)),
                      email: user.email
                    });
                    setShowHabiliter(false);
                    setLoading(true);
                    await loadProfil();
                  } catch {
                  } finally {
                    setHabBusy(false);
                  }
                }}>
                  <div className="modal-header">
                    <h5 className="modal-title">Habiliter comme médecin</h5>
                    <button type="button" className="btn-close" aria-label="Fermer" onClick={() => setShowHabiliter(false)}></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-2">
                      <label className="form-label">Titre</label>
                      <select className="form-select" value={habTitre} onChange={(e)=>setHabTitre(e.target.value)} required>
                        <option value="">—</option>
                        <option value="Dr">Docteur (Dr)</option>
                        <option value="Pr">Professeur (Pr)</option>
                      </select>
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Matricule</label>
                      <input className="form-control" value={habMatricule} onChange={(e)=>setHabMatricule(e.target.value)} required maxLength={60} />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Spécialités</label>
                      <Select
                        classNamePrefix="select"
                        isMulti
                        isSearchable
                        placeholder="Sélectionner les spécialités"
                        options={(specialites || []).map(s => ({ value: s.id, label: s.libelle_specialite }))}
                        value={habSpecs}
                        onChange={(vals)=>setHabSpecs(vals || [])}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        noOptionsMessage={() => 'Aucune spécialité'}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary" onClick={()=>setShowHabiliter(false)} disabled={habBusy}>Annuler</button>
                    <button type="submit" className="btn btn-primary" disabled={habBusy || !habTitre || !habMatricule.trim() || !(habSpecs && habSpecs.length)}>
                      {habBusy ? 'Enregistrement...' : 'Valider'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showConfirmRevoke && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmation</h5>
                  <button type="button" className="btn-close" aria-label="Fermer" onClick={() => setShowConfirmRevoke(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning d-flex align-items-start" role="alert">
                    <span className="me-2" style={{ lineHeight: 1.2 }}>
                      <FontAwesomeIcon icon={solid('triangle-exclamation')} />
                    </span>
                    <div>
                      <b>Supprimer l'habilitation ?</b>
                      <div className="small mt-1">Cette action videra le titre et le matricule et supprimera toutes les spécialités.</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowConfirmRevoke(false)} disabled={revokeBusy}>Annuler</button>
                  <button type="button" className="btn btn-danger" disabled={revokeBusy} onClick={async () => {
                    setRevokeBusy(true);
                    try {
                      await AdminDataService.revokeHabilitation({ targetEmail: urlEmail, email: user.email });
                      setShowConfirmRevoke(false);
                      setLoading(true);
                      await loadProfil();
                    } catch {
                    } finally {
                      setRevokeBusy(false);
                    }
                  }}>
                    {revokeBusy ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showRecetteRequiresAdmin && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Information</h5>
                  <button type="button" className="btn-close" aria-label="Fermer" onClick={() => setShowRecetteRequiresAdmin(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-info d-flex align-items-start" role="alert">
                    <span className="me-2" style={{ lineHeight: 1.2 }}>
                      <FontAwesomeIcon icon={solid('circle-info')} />
                    </span>
                    <div>La visibilité sur les recettes requiert l'habilitation d'administrateur.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={() => setShowRecetteRequiresAdmin(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default UserProfil;